import { createContext, useCallback, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
// utils
import ApiServiceFecth from "service/ApiServiceFecth";
import AuthService from "service/AuthService";
import axios from "../utils/axios";
import endPoints from "endPoints/endPoints";
import { sanitizeShortcuts } from "utils/sidebarShortcuts";

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  permissions: [],
  areaPermissions: [],
  menuShortcuts: [],
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, permissions, areaPermissions, menuShortcuts = [] } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      permissions,
      areaPermissions,
      menuShortcuts,
    };
  },
  LOGIN: (state, action) => {
    const { user, permissions, areaPermissions, menuShortcuts = [] } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      permissions,
      areaPermissions,
      menuShortcuts,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
    permissions: [],
    areaPermissions: [],
    menuShortcuts: [],
  }),
  SHORTCUTS: (state, action) => ({
    ...state,
    menuShortcuts: action.payload.menuShortcuts || [],
  }),
  PERMISSIONS: (state, action) => ({
    ...state,
    permissions: action.payload.permissions || [],
    areaPermissions: action.payload.areaPermissions || state.areaPermissions,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  checkAccess: () => Promise.resolve(),
  register: () => Promise.resolve(),
  getPermissions: () => Promise.resolve(),
  saveMenuShortcuts: () => Promise.resolve(),
});

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchMenuShortcuts = useCallback(async () => {
    const response = await ApiServiceFecth.get({ url: endPoints.admin.myShortcuts.base });
    if (!response.ok) {
      return [];
    }
    const payload = await response.json();
    const menuShortcuts = sanitizeShortcuts(payload?.data || []);
    dispatch({
      type: "SHORTCUTS",
      payload: { menuShortcuts },
    });
    return menuShortcuts;
  }, []);

  useEffect(() => {
    const initialize = async () => {
      // dispatch({
      //   type: "INITIALIZE",
      //   payload: {
      //     isAuthenticated: true,
      //     user: { name: 'julian' },
      //     permissions: []
      //   },
      // });
      try {
        if (AuthService.isAuthenticated() && AuthService.isTokenValid()) {
          await ApiServiceFecth.getUser()
            .then((response) => {
              if (response.ok) {
                return response.json();
              }
              AuthService.storeSessionData(null);
              throw Error(response);
            })
            .then(async (response) => {
              const { user, permissions, area_permissions: areaPermissions = [] } = response;
              const menuShortcuts = await fetchMenuShortcuts();
              dispatch({
                type: "INITIALIZE",
                payload: {
                  isAuthenticated: true,
                  user,
                  permissions,
                  areaPermissions,
                  menuShortcuts,
                },
              });
            })
            .catch((error) => {
              const message =
                (error.response && error.response.data.message) ||
                "Credenciales inválidos";
              throw Error(message);
            });
        } else {
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
              permissions: [],
              areaPermissions: [],
            },
          });
        }
      } catch (err) {
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
            permissions: [],
            areaPermissions: [],
          },
        });
      }
    };

    initialize();
  }, [fetchMenuShortcuts]);

  const checkAccess = useCallback(
    (userPermission = "") => {
      const { permissions } = state;
      if (userPermission) {
        if (permissions.includes(userPermission)) {
          return true;
        }
        return false;
      }
      return true;
    },
    [state]
  );

  const getPermissions = async () => {
    await ApiServiceFecth.getUser()
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw Error(response.statusText);
      })
      .then((response) => {
        const { permissions, area_permissions: areaPermissions = [] } = response;

        dispatch({
          type: "PERMISSIONS",
          payload: {
            permissions,
            areaPermissions,
          },
        });
      })
      .catch((error) => {
        const message =
          (error.response && error.response.data.message) ||
          "Credenciales inválidos";
        throw Error(message);
      });
  };

  const saveMenuShortcuts = useCallback(async (shortcuts = []) => {
    const normalized = sanitizeShortcuts(shortcuts);
    const response = await ApiServiceFecth.put({
      url: endPoints.admin.myShortcuts.base,
      data: { shortcuts: normalized },
    });

    if (!response.ok) {
      throw new Error("No fue posible guardar los accesos directos.");
    }

    dispatch({
      type: "SHORTCUTS",
      payload: { menuShortcuts: normalized },
    });
  }, []);

  const login = async (email, password) => {
    await ApiServiceFecth.login({ email, password })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw Error(response.statusText);
      })
      .then(async (response) => {
        const { token: accessToken, user, permissions, area_permissions: areaPermissions = [] } = response;

        AuthService.storeSessionData(accessToken);

        const menuShortcuts = await fetchMenuShortcuts();
        dispatch({
          type: "LOGIN",
          payload: {
            user,
            permissions,
            areaPermissions,
            menuShortcuts,
          },
        });
      })
      .catch((error) => {
        const message =
          (error.response && error.response.data.message) ||
          "Credenciales inválidos";
        throw Error(message);
      });
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post("/api/account/register", {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    window.localStorage.setItem("accessToken", accessToken);
    dispatch({
      type: "REGISTER",
      payload: {
        user,
      },
    });
  };

  const logout = async () => {
    await ApiServiceFecth.logout()
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw Error(response.statusText);
      })
      .then(() => {
        AuthService.storeSessionData(null);
        dispatch({ type: "LOGOUT" });
      })
      .catch((error) => {
        const message = error.response && error.response.data.message;
        throw Error(message);
      });
  };

  const resetPassword = () => { };

  const updateProfile = () => { };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        resetPassword,
        checkAccess,
        updateProfile,
        getPermissions,
        saveMenuShortcuts,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
